import { Fragment, useMemo, useState } from "react";
import { Chip, CircularProgress, Grid, Icon, IconButton, InputLabel, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import { get } from "utils/WebService";
import { useMaterialUIController } from "context";
import { coresDoTema } from "assets/coresTema";
import { baixarCsv, decimalCsv } from "views/coordenador/relatorios/analiseTempo/csv";

const rotulo = { px: 1, pb: 0.5, pt: 0.2 };
const celula = { verticalAlign: "top" };
const MAX_GRUPOS_NA_TELA = 200;

const numero = (n) => Number(n || 0).toLocaleString("pt-BR");
const moeda = (v) => Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const normalizar = (t) => String(t || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

/** Abre a ficha numa aba nova do navegador, só para consulta (FichaAvulsa), com o login e o tema desta aba. */
const abrirFicha = (linha) => {
    [["temp_share_user", "user"], ["temp_share_menus", "userMenus"], ["temp_share_tema", "tema"]].forEach(([destino, origem]) => {
        const valor = sessionStorage.getItem(origem);
        if (valor) localStorage.setItem(destino, valor);
    });
    window.open(`/system?view=ficha&devedor=${encodeURIComponent(linha.idDevedor)}&ficha=${encodeURIComponent(linha.ficha)}`, "_blank");
};

const COLUNAS_CSV = [
    ["Código TMK (honorários)", "codigoTmk"], ["PK_Cliente TMK", "pkTmk"], ["Nome TMK", "nomeTmk"], ["CPF/CNPJ TMK", "documentoTmk"],
    ["Processo(s) TMK", "processosTmk"], ["Cliente", "cliente"], ["Cliente(s) no TMK", "clientesTmk"], ["Ficha", "ficha"],
    ["Devedor", "devedor"], ["Código do cliente", "codigoCliente"], ["CPF/CNPJ", "documento"], ["Carteira", "carteira"],
    ["Situação", "situacao"], ["Contratos", "contratos"], ["Saldo", "saldo"], ["Acordos", "acordos"],
    ["Fichas principais do devedor", "fichasPrincipais"], ["Conferência no TMK", "conferencia"], ["Indicação", "indicacao"],
];

/**
 * Fichas de honorários do TMK que a migração pôs em mais de um cliente (o mesmo PK_Cliente em clientes diferentes), para
 * o pessoal conferir e ajustar: uma linha por ficha do sistema, agrupadas pelo Código do TMK, com o que o TMK diz e se o
 * PK_Cliente e o processo batem com ficha principal daquele cliente. Só leitura (GET /migracao/honorarios-em-mais-de-um-cliente).
 */
export default function HonorariosEmMaisDeUmCliente({ showSnackbar }) {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    const cores = coresDoTema(darkMode);
    const [carregando, setCarregando] = useState(false);
    const [resultado, setResultado] = useState(null);
    const [busca, setBusca] = useState("");

    const montar = async () => {
        setCarregando(true);
        try {
            setResultado(await get("/migracao/honorarios-em-mais-de-um-cliente"));
        } catch (e) {
            showSnackbar(`Não foi possível montar a lista: ${e.message || "erro desconhecido"}`, false);
        } finally {
            setCarregando(false);
        }
    };

    // Grupos pelo Código do TMK, na ordem do backend; a busca vale para qualquer campo do grupo
    const grupos = useMemo(() => {
        const porCodigo = new Map();
        (resultado?.linhas || []).forEach(l => {
            if (!porCodigo.has(l.codigoTmk)) porCodigo.set(l.codigoTmk, []);
            porCodigo.get(l.codigoTmk).push(l);
        });
        const termo = normalizar(busca.trim());
        const todos = [...porCodigo.values()];
        if (!termo) return todos;
        return todos.filter(g => g.some(l => normalizar(
            [l.codigoTmk, l.pkTmk, l.nomeTmk, l.documentoTmk, l.processosTmk, l.cliente, l.devedor, l.codigoCliente, l.documento, `#${l.ficha}`].join(" ")
        ).includes(termo)));
    }, [resultado, busca]);

    const maisProvaveis = (resultado?.linhas || []).filter(l => l.indicacao === "Mais provável").length;

    const baixar = () => {
        const linhas = [COLUNAS_CSV.map(([titulo]) => titulo)];
        grupos.flat().forEach(l => linhas.push(COLUNAS_CSV.map(([, campo]) => (campo === "saldo" ? decimalCsv(l.saldo) : l[campo]))));
        baixarCsv(`honorarios-em-mais-de-um-cliente-${new Date().toISOString().slice(0, 10)}.csv`, linhas);
    };

    return (
        <MDBox>
            <MDTypography variant="h6" color={darkMode ? "white" : "dark"}>Conferência: honorários em mais de um cliente</MDTypography>
            <MDTypography variant="caption" color="text" display="block">
                Fichas de honorários do TMK que a migração pôs em mais de um cliente: cada cliente é migrado olhando só as fichas dele
                (mesmo PK_Cliente e, se houver, mesmo processo), e o mesmo PK_Cliente pode existir em outro cliente. Uma linha por
                ficha do sistema, agrupadas pelo Código do TMK. "Conferência no TMK" diz se o PK_Cliente e o processo batem com uma
                ficha principal daquele cliente; "Mais provável" aparece quando só uma das fichas do grupo tem o melhor resultado. Só
                leitura: os ajustes são feitos nas fichas.
            </MDTypography>

            <Grid container spacing={2} alignItems="flex-end" mt={0.5}>
                <Grid item xs={12} md={6}>
                    <InputLabel sx={rotulo}>Buscar</InputLabel>
                    <MDInput fullWidth size="small" value={busca} onChange={e => setBusca(e.target.value)} disabled={!resultado}
                        placeholder="Código ou PK do TMK, nome, CPF/CNPJ, cliente, processo ou nº da ficha" />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <InputLabel sx={rotulo}>&nbsp;</InputLabel>
                    <MDButton variant="contained" color="info" size="medium" fullWidth onClick={montar} disabled={carregando} sx={{ height: 38 }}>
                        {carregando ? <CircularProgress size={16} color="inherit" /> : resultado ? "Montar de novo" : "Montar lista"}
                    </MDButton>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <InputLabel sx={rotulo}>&nbsp;</InputLabel>
                    <MDButton variant="outlined" color="info" size="medium" fullWidth onClick={baixar} disabled={!grupos.length} sx={{ height: 38 }}>
                        Baixar planilha (CSV)
                    </MDButton>
                </Grid>
            </Grid>

            {resultado && (
                <MDTypography variant="caption" color="text" fontWeight="medium" display="block" mt={1.5}>
                    {resultado.honorariosTmk === 0
                        ? "Nenhuma ficha de honorários do TMK está em mais de um cliente."
                        : `${numero(resultado.honorariosTmk)} ficha(s) de honorários do TMK em ${numero(resultado.fichas)} ficha(s) do sistema · ${numero(maisProvaveis)} com indicação "Mais provável"`
                          + (busca.trim() ? ` · ${numero(grupos.length)} na busca` : "")
                          + (grupos.length > MAX_GRUPOS_NA_TELA ? ` · mostrando as ${MAX_GRUPOS_NA_TELA} primeiras (a planilha leva todas)` : "")}
                </MDTypography>
            )}

            {grupos.length > 0 && (
                <TableContainer sx={{ mt: 1, maxHeight: 560, border: `1px solid ${cores.borda}`, borderRadius: 1 }}>
                    <Table size="small" stickyHeader>
                        <TableHead sx={{ display: "table-header-group" }}>
                            <TableRow>
                                {["Cliente", "Ficha", "Devedor", "Débitos e acordos", "Conferência no TMK"].map(t => (
                                    <TableCell key={t} sx={{ backgroundColor: cores.painel }}>
                                        <MDTypography variant="caption" fontWeight="bold" color="text">{t}</MDTypography>
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {grupos.slice(0, MAX_GRUPOS_NA_TELA).map(grupo => {
                                const tmk = grupo[0];
                                return (
                                    <Fragment key={tmk.codigoTmk}>
                                        <TableRow>
                                            <TableCell colSpan={5} sx={{ backgroundColor: cores.painelEscuro }}>
                                                <MDTypography variant="caption" fontWeight="bold" color="text">
                                                    TMK {tmk.codigoTmk} · PK_Cliente {tmk.pkTmk || "-"} · {tmk.nomeTmk || "-"}{tmk.documentoTmk ? ` · ${tmk.documentoTmk}` : ""}
                                                </MDTypography>
                                                <MDTypography variant="caption" color="text" display="block">
                                                    {tmk.processosTmk ? `Processo(s): ${tmk.processosTmk}` : "Sem processo na BVJuridico"}
                                                </MDTypography>
                                            </TableCell>
                                        </TableRow>
                                        {grupo.map(l => (
                                            <TableRow key={l.ficha}>
                                                <TableCell sx={celula}>
                                                    <MDTypography variant="caption" fontWeight="bold" color="text" display="block">{l.cliente || "-"}</MDTypography>
                                                    <MDTypography variant="caption" color="text" display="block">{l.clientesTmk ? `TMK: ${l.clientesTmk}` : "Sem De-Para com o TMK"}</MDTypography>
                                                </TableCell>
                                                <TableCell sx={{ ...celula, whiteSpace: "nowrap" }}>
                                                    <MDBox display="flex" alignItems="center" gap={0.5}>
                                                        <MDTypography variant="caption" fontWeight="bold" color="text">#{l.ficha}</MDTypography>
                                                        <Tooltip title="Abrir a ficha numa aba nova (só consulta)">
                                                            <IconButton size="small" color="info" onClick={() => abrirFicha(l)} sx={{ p: 0.25 }}>
                                                                <Icon fontSize="small">open_in_new</Icon>
                                                            </IconButton>
                                                        </Tooltip>
                                                    </MDBox>
                                                    <MDTypography variant="caption" color="text" display="block">{[l.carteira, l.situacao].filter(Boolean).join(" · ")}</MDTypography>
                                                </TableCell>
                                                <TableCell sx={celula}>
                                                    <MDTypography variant="caption" fontWeight="bold" color="text" display="block">{l.devedor}</MDTypography>
                                                    <MDTypography variant="caption" color="text" display="block">
                                                        {[l.codigoCliente && `Cód. ${l.codigoCliente}`, l.documento].filter(Boolean).join(" · ") || "-"}
                                                    </MDTypography>
                                                    <MDTypography variant="caption" color={l.fichasPrincipais ? "text" : "error"} display="block">
                                                        {l.fichasPrincipais ? `${numero(l.fichasPrincipais)} ficha(s) principal(is)` : "Sem ficha principal"}
                                                    </MDTypography>
                                                </TableCell>
                                                <TableCell sx={celula}>
                                                    <MDTypography variant="caption" color="text" display="block">{numero(l.contratos)} débito(s) · saldo {moeda(l.saldo)}</MDTypography>
                                                    <MDTypography variant="caption" color="text" display="block">{numero(l.acordos)} acordo(s)</MDTypography>
                                                </TableCell>
                                                <TableCell sx={celula}>
                                                    <Chip size="small" variant="outlined" color={l.indicacao === "Mais provável" ? "success" : "warning"} label={l.indicacao} sx={{ mb: 0.5 }} />
                                                    <MDTypography variant="caption" color="text" display="block">{l.conferencia}</MDTypography>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </Fragment>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </MDBox>
    );
}
